import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { to: "/services", label: "Services" },
  { to: "/fleet", label: "Fleet" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar({ transparent = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const isTransparent = transparent && !scrolled && !open;

  return (
    <header
      data-testid="global-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ease-out ${
        isTransparent
          ? "bg-transparent border-b border-transparent"
          : "bg-midnight/95 backdrop-blur-md border-b border-white/10"
      }`}
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12 h-20 md:h-[88px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-brand-logo"
          className="flex items-center gap-3.5 group"
        >
          <span
            className="w-9 h-9 md:w-10 md:h-10 grid place-items-center border border-gold/40/60 text-warm-white font-medium text-[13px] tracking-[0.15em] transition-colors duration-500 group-hover:border-gold"
          >
            SJ
          </span>
          <span
            className="text-warm-white text-[13px] md:text-[14px] tracking-[0.28em] uppercase font-medium"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Sydney James
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-11">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-[11px] tracking-[0.28em] uppercase font-medium transition-colors duration-300 py-2 ${
                  isActive
                    ? "text-warm-white"
                    : "text-warm-white/60 hover:text-warm-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-0.5 left-0 h-px bg-gold transition-all duration-500 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center">
          <Link
            to="/book"
            data-testid="nav-cta-book-journey"
            className="group relative inline-flex items-center gap-3 border border-gold/40 text-warm-white px-6 py-3 text-[10.5px] tracking-[0.28em] uppercase font-medium transition-all duration-500 hover:border-gold hover:bg-gold/[0.05]"
          >
            <span>Book Your Journey</span>
            <span className="w-1.5 h-1.5 rounded-full bg-gold transition-transform duration-500 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-menu-trigger"
          className="md:hidden inline-flex items-center justify-center w-11 h-11 text-warm-white transition-colors duration-300"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="w-6 h-6" strokeWidth={1.25} /> : <Menu className="w-6 h-6" strokeWidth={1.25} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="nav-mobile-drawer"
        className={`lg:hidden fixed inset-x-0 top-20 bottom-0 bg-midnight transition-all duration-500 ease-out ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
      >
        <div className="max-w-[92rem] mx-auto px-6 py-10 flex flex-col h-full">
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className="group py-5 border-b border-white/10 flex items-center justify-between text-warm-white"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <span className="font-display text-2xl">{l.label}</span>
                <span className="w-2 h-2 rounded-full bg-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto pt-10">
            <Link
              to="/book"
              data-testid="nav-mobile-cta"
              className="block w-full text-center border border-gold/40 py-4 text-warm-white text-[11px] tracking-[0.28em] uppercase font-medium transition-colors duration-500 hover:bg-gold/[0.08]"
            >
              Book Your Journey
            </Link>
            <div className="mt-8 grid grid-cols-2 gap-6 text-[10.5px] tracking-[0.22em] uppercase text-warm-white/50 font-medium">
              <a href="tel:+61400000000" data-testid="nav-mobile-phone" className="hover:text-warm-white transition-colors">
                +61 400 000 000
              </a>
              <a href="mailto:info@sydneyjames.com.au" data-testid="nav-mobile-email" className="hover:text-warm-white transition-colors text-right">
                Email Concierge
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
